import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import { CoArchitectLogo, Plus, ImageIcon, Sparkles, Trash, LogOut, Home, Download } from '../components/Icons';
import BottomNav from '../components/BottomNav';
import { mixpanel } from '../lib/mixpanel';

interface Project {
  id: string;
  name: string;
  originalImageUrl?: string;
  createdAt: string;
  generations?: Array<{ id: string }>;
}

interface GeneratedImage {
  id: string;
  projectId: string;
  resultImageUrl: string;
  productDescription: string;
  createdAt: string;
}

interface ContextMenu {
  x: number;
  y: number;
  image: GeneratedImage;
}

async function downloadImage(url: string, filename: string) {
  mixpanel.track('image_downloaded', {});
  try {
    const proxyUrl = `${API}/proxy/image?url=${encodeURIComponent(url)}`;
    const res  = await fetch(proxyUrl);
    const blob = await res.blob();
    const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file] }); return; } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') return;
      }
    }
    const href = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  } catch {
    window.open(url, '_blank');
  }
}

function Library() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects]             = useState<Project[]>([]);
  const [genImages, setGenImages]           = useState<GeneratedImage[]>([]);
  const [loading, setLoading]               = useState(true);
  const [confirmDelete, setConfirmDelete]   = useState<Project | null>(null);
  const [deleting, setDeleting]             = useState(false);
  const [contextMenu, setContextMenu]       = useState<ContextMenu | null>(null);
  const contextMenuRef                      = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/projects`),
      axios.get(`${API}/generations`),
    ]).then(([pRes, gRes]) => {
      setProjects(pRes.data);
      setGenImages(gRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    if (contextMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [contextMenu]);

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${confirmDelete.id}`);
      setProjects(prev => prev.filter(p => p.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch { /* silently ignore */ } finally { setDeleting(false); }
  };

  const handleLogout = () => { logout(); navigate('/', { replace: true }); };

  const openContextMenu = (e: React.MouseEvent, image: GeneratedImage) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, image });
  };

  const handleSaveImage = async (image: GeneratedImage) => {
    setContextMenu(null);
    await downloadImage(image.resultImageUrl, `ai-preview-${image.id.slice(0, 8)}.jpg`);
  };

  const handleUseForUpdates = (image: GeneratedImage) => {
    setContextMenu(null);
    navigate(`/project/${image.projectId}`);
  };

  return (
    <div className="page-root">

      {/* Header */}
      <header className="app-header">
        <div className="header-inner">
          <div className="logo-lockup">
            <div className="logo-mark-sm">
              <CoArchitectLogo size={28} />
            </div>
            <span className="heading-brand logo-text-sm">Co-Architect</span>
          </div>
          <div className="header-actions-row">
            <button className="header-action-btn" onClick={() => navigate('/home')} aria-label="Home">
              <Home size={18} />
            </button>
            <button className="header-action-btn" onClick={handleLogout} aria-label="Sign out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="page-scroll">
        <div className="dashboard-content">

          {/* ── My Projects ── */}
          <div className="section-header" style={{ marginBottom: 4 }}>
            <h3 className="heading-sm">My Projects</h3>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/new-project')} aria-label="Create new project">
              <Plus size={15} />Create New Project
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
              <div className="spinner" style={{ width: 32, height: 32 }} />
            </div>
          ) : (
            <div className="projects-grid">
              {projects.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><ImageIcon size={28} /></div>
                  <h3>No projects yet</h3>
                  <p>Go to Home and pick a space, or create your own project.</p>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/home')} style={{ marginTop: 8 }}>
                    Go to Home
                  </button>
                </div>
              ) : (
                projects.map(project => (
                  <ProjectCard key={project.id} project={project} onDelete={() => setConfirmDelete(project)} />
                ))
              )}
            </div>
          )}

          {/* ── Generated Images ── */}
          {!loading && genImages.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 className="heading-sm">Generated Images</h3>
                <span className="caption">{genImages.length} image{genImages.length !== 1 ? 's' : ''}</span>
              </div>
              <p className="caption" style={{ marginBottom: 12, color: 'var(--text-faint)' }}>
                Tap to view · Right-click or long-press to save or use for further updates
              </p>
              <div className="gen-images-grid">
                {genImages.map(img => (
                  <div
                    key={img.id}
                    className="gen-image-item"
                    onClick={e => openContextMenu(e, img)}
                    onContextMenu={e => openContextMenu(e, img)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && openContextMenu(e as any, img)}
                    title={img.productDescription}
                  >
                    <img src={img.resultImageUrl} alt={img.productDescription} loading="lazy" />
                    <div className="gen-image-overlay">
                      <Sparkles size={14} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="gen-context-menu"
          style={{ top: contextMenu.y, left: Math.min(contextMenu.x, window.innerWidth - 220) }}
        >
          <button
            type="button"
            className="gen-context-item"
            onClick={() => handleSaveImage(contextMenu.image)}
          >
            <Download size={16} />
            Save Image
          </button>
          <button
            type="button"
            className="gen-context-item"
            onClick={() => handleUseForUpdates(contextMenu.image)}
          >
            <Sparkles size={16} />
            Use for Further Updates
          </button>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="annotator-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="annotator-sheet" onClick={e => e.stopPropagation()} style={{ gap: 0 }}>
            <div style={{ padding: '20px 20px 0', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Trash size={22} color="#ef4444" />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Delete Project</h3>
              <p style={{ margin: '8px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                "{confirmDelete.name}" and all its AI previews will be permanently deleted.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: 20 }}>
              <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="btn" style={{ flex: 1, background: '#ef4444', color: '#fff', border: 'none' }} onClick={handleDeleteConfirmed} disabled={deleting}>
                {deleting ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function ProjectCard({ project, onDelete }: { project: Project; onDelete: () => void }) {
  const count = project.generations?.length || 0;
  const date  = new Date(project.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return (
    <Link to={`/project/${project.id}`} className="project-card" style={{ position: 'relative' }}>
      <div className="project-card-image">
        {project.originalImageUrl
          ? <img src={project.originalImageUrl} alt={project.name} loading="lazy" />
          : <div className="project-card-image-placeholder"><ImageIcon size={32} /></div>}
      </div>
      <div className="project-card-body">
        <p className="project-card-name">{project.name}</p>
        <div className="project-card-meta">
          <span className="project-card-count"><Sparkles size={11} />{count} {count === 1 ? 'preview' : 'previews'}</span>
          <span style={{ color: 'var(--border)', fontSize: 10 }}>·</span>
          <span className="caption">{date}</span>
        </div>
      </div>
      <button type="button" className="project-card-delete-btn" aria-label="Delete project"
        onClick={e => { e.preventDefault(); e.stopPropagation(); onDelete(); }}>
        <Trash size={14} />
      </button>
    </Link>
  );
}

export default Library;
